// Medical Portal - Raise Query
//
// A three-part form on one page:
//   1. Query type   - Patient-Specific or General Service. A triage tag;
//                     both types collect the same fields underneath.
//   2. Event details - when, where and who detected it.
//   3. Description   - what happened, plus optional evidence.
//
// WHY ONE PAGE AND NOT A WIZARD: the whole thing is nine fields. Splitting
// nine fields across three screens hides from the user how much is left,
// and makes them click Next twice to find out. The rail at the top marks
// progress instead, and every card stays open and editable.
//
// WHAT CHANGED IN THE REDESIGN: the submit button used to sit there
// disabled with no explanation - the single worst thing about the old
// page, because a user with one empty field had no way to find which.
// `missing` below names the outstanding fields, the footer lists them,
// and each card shows a count of what it is still waiting for.
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {
  MP, MONO, DISPLAY, Alert, SectionCard, CardHeader, FieldLabel,
  groupByVisit, prettyDate,
} from './shared';
import { BiIcon } from '../../../layouts/SidebarNav';
import { useAuth } from '../../../context/AuthContext';
import { useCreateRcaInvestigationMutation } from '../../../store/queriesApi';
import { useListIssueCategoriesQuery } from '../../../store/siteContentApi';
import {
  useGetSalesByCustomerQuery,
  useGetSalesByCustomerAndDoctorQuery,
} from '../../../store/salesApi';

// Every query raised through this portal IS a customer complaint - that is
// what the portal is. The field used to ask the user to pick it from eight
// options, seven of which (internal audit, equipment alarm, lab QC...)
// describe something the LAB detected, not the centre. It is now a constant
// so the QTQMS payload keeps the key it has always had.
const QUERY_SOURCE = 'Customer complaint';

// Must match OTHER_SUBCATEGORY in backend/src/controllers/supportController.js
// and the constant of the same name in pages/Support.js. The three have to
// agree or an "Other" sub-category arrives as a literal key.
const OTHER_SUBCATEGORY = '__other';

// ISO-style default for datetime-local = current local time rounded to the
// nearest minute. Field is editable; this is just a head-start.
const nowLocal = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Halves of a 'YYYY-MM-DDTHH:mm' value, for the split date/time inputs.
// Both tolerate an empty string so a cleared field can't produce 'undefined'
// in the submitted payload.
const datePart = (v = '') => (v.includes('T') ? v.slice(0, v.indexOf('T')) : v);
const timePart = (v = '') => (v.includes('T') ? v.slice(v.indexOf('T') + 1, v.indexOf('T') + 6) : '');
// Guarded against a non-date `d`: a native date input only ever hands
// back '' or 'YYYY-MM-DD', but anything else would otherwise be glued
// to a time and posted as the detection timestamp.
const joinDateTime = (d, t) => (/^\d{4}-\d{2}-\d{2}$/.test(d || '') ? `${d}T${t || '00:00'}` : '');

// `prefillVisit` lands here when the user opens RaiseQuery from the
// "Raise Query" button inside ReportDetailModal — we force the nature
// to 'patient' and pass the visit through to the form so the patient
// picker shows it pre-selected.
// A three-step wizard, not a stack of cards.
//
// The old page put every field on one scroll. Nine fields does not sound
// like much, but with a patient picker, a five-row textarea and a dropzone
// it ran past two screens, and the user could not see where they were in
// it. One step at a time, with a rail that doubles as a running summary of
// what has been answered.
//
// The rail is NOT decorative: each completed step shows its own answer
// underneath, and clicking it jumps back to edit. Nothing is hidden - it is
// paged, and every page is one glance away.
const STEP_IDS = ['type', 'details', 'describe'];

const RaiseQuery = ({ onBack, prefillVisit = null }) => {
  const { user } = useAuth();
  const clientId = user?.client_id;
  const [createRca, { isLoading }] = useCreateRcaInvestigationMutation();
  const [toast, setToast] = useState({ open: false, msg: '', tone: 'success' });

  // Arriving from a report's "Raise Query" button means the type is already
  // known, so that step is answered and we open on the next one.
  const [nature, setNature] = useState(prefillVisit ? 'patient' : null);
  const [step, setStep] = useState(prefillVisit ? 1 : 0);
  const isPatient = nature === 'patient';

  // Who is raising this, and from where. Both come off the signed-in
  // account, so the two fields open filled in and are only touched when
  // somebody needs to correct them.
  //
  // There is NO branch or location on the user record - see sanitizeUser in
  // backend/src/controllers/authController.js. The centre's own name plus
  // its client id is the closest true answer, and it beats what was here
  // before: a dropdown of eight hard-coded cities that had nothing to do
  // with the signed-in centre.
  const centreName = user?.lab_name || user?.company_name || user?.name || '';
  const defaultDetector = [user?.name, user?.designation].filter(Boolean).join(', ');
  const defaultBranch = [centreName, clientId].filter(Boolean).join(' — ');

  const defaultDetected = useMemo(nowLocal, []);
  const [form, setForm] = useState({
    // Pre-select the visit when one came in via the report-detail modal.
    // The PatientPicker accepts a value that isn't on its first page —
    // Autocomplete renders the selected option through getOptionLabel
    // even when it's not in the loaded options list.
    selectedVisit: prefillVisit,
    detectedAt: defaultDetected,
    detector: defaultDetector,
    branch: defaultBranch,
    category: '',
    subcategory: '',
    subcategoryOther: '',
    description: '',
    evidence: [],          // array of File — multi-attachment upload
    // "I'd like a TrustLab doctor to call me" — and the number to call,
    // prefilled from the account and editable.
    callback: false,
    callbackPhone: user?.phone || '',
  });

  // If the prefill arrives later (e.g. parent re-renders after navigation),
  // adopt it — but only when the user hasn't picked a different visit
  // themselves. Compare by visitNo so a re-render with the same prefill
  // is a no-op.
  useEffect(() => {
    if (!prefillVisit) return;
    setForm((f) => {
      if (f.selectedVisit?.visitNo === prefillVisit.visitNo) return f;
      return { ...f, selectedVisit: prefillVisit };
    });
  }, [prefillVisit]);

  // The same admin-managed lists the public Support form uses, so a
  // category added in the admin panel shows up in both without a second
  // place to maintain.
  const { data: issueTypes } = useListIssueCategoriesQuery();
  const categories = useMemo(() => issueTypes?.categories ?? [], [issueTypes]);
  const subcategories = useMemo(() => issueTypes?.subcategories ?? [], [issueTypes]);

  // Sub-categories of the chosen category. Empty when that category has
  // none — an admin can add them to one category without being forced to
  // add them to all of them.
  const subOptions = form.category
    ? subcategories.filter((sc) => sc.parent_key === form.category)
    : [];

  // Changing the category invalidates whatever sub-category was picked:
  // it belongs to the category that was just replaced.
  useEffect(() => {
    setForm((f) => (
      f.subcategory || f.subcategoryOther
        ? { ...f, subcategory: '', subcategoryOther: '' }
        : f
    ));
  }, [form.category]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // What each step is still waiting for. Naming the fields is the point:
  // the old page disabled Submit and left the user to find the empty one.
  const missingByStep = [
    nature ? [] : ['Query type'],
    [
      isPatient && !form.selectedVisit ? 'Patient' : null,
      !form.detectedAt                 ? 'Date & time detected' : null,
      !form.detector.trim()            ? 'Detector' : null,
      !form.branch.trim()              ? 'Branch & section' : null,
    ].filter(Boolean),
    [
      !form.category ? 'Issue category' : null,
      form.subcategory === OTHER_SUBCATEGORY && !form.subcategoryOther.trim()
        ? 'A few words for “Other”' : null,
      !form.description.trim() ? 'Description' : null,
      form.callback && String(form.callbackPhone || '').replace(/\D/g, '').length < 10
        ? 'Number for the doctor to call' : null,
    ].filter(Boolean),
  ];
  const stepDone = (n) => missingByStep[n].length === 0;
  const allDone = missingByStep.every((m) => m.length === 0);

  // A step is reachable once every step before it is answered, so the rail
  // can't drop someone into Review with an empty form.
  const canReach = (n) => missingByStep.slice(0, n).every((m) => m.length === 0);
  const go = (n) => { if (canReach(n)) setStep(n); };

  const onSubmit = async () => {
    if (!clientId) {
      setToast({ open: true, msg: 'Your account is missing a Client ID. Contact admin.', tone: 'error' });
      return;
    }

    // Multipart body matching the qtqms spec:
    //   clientId, visitor_id, query_type   — top-level fields
    //   state                                — JSON string with nested incident
    //   files[]                              — zero-or-more attachments
    // visitor_id is only appended for patient-specific queries so
    // general-service rows don't store a blank.
    const fd = new FormData();
    fd.append('clientId', clientId);
    fd.append('query_type', isPatient ? 'patient_specific' : 'general_service');
    if (isPatient && form.selectedVisit) {
      const v = form.selectedVisit;
      fd.append('visitor_id', String(v.visitNo));
      // The patient behind the visit, so the desk (Trust OPM) and Quality
      // (QTQMS) see who the query is about without opening the LIS.
      fd.append('patient', JSON.stringify({
        visitNo: v.visitNo, uhid: v.uhid || '', name: v.patient || '', age: v.age || '', gender: v.gender || '',
        visitDate: v.billDate || v.openDate || '', referral: v.referral && v.referral !== '—' ? v.referral : '',
        lab: v.lab || '', tests: (v.tests || []).map((t) => t.name).filter(Boolean).slice(0, 40),
      }));
    }

    const state = {
      incident: {
        // Both keys are kept so the upstream contract is unchanged: QTQMS
        // has always received them. `source` is now a constant and
        // `occurredAt` was already optional and nullable.
        source: QUERY_SOURCE,
        detectedAt: form.detectedAt,
        occurredAt: null,
        detector: form.detector.trim(),
        branch: form.branch.trim(),
        // Same keys the support ticket uses, so the two routes into the
        // helpdesk classify a complaint the same way.
        category: form.category,
        subcategory: form.subcategory || '',
        subcategory_other:
          form.subcategory === OTHER_SUBCATEGORY ? form.subcategoryOther.trim() : '',
        description: form.description.trim(),
      },
    };
    fd.append('state', JSON.stringify(state));
    if (form.callback) {
      // The doctor callback goes to Trust OPM with the complaint; the desk
      // arranges the call. Not part of the QTQMS record.
      fd.append('callback_phone', String(form.callbackPhone || '').trim());
    }

    for (const file of form.evidence) {
      fd.append('files[]', file, file.name);
    }

    try {
      const res = await createRca(fd).unwrap();
      // The backend also registers the query as a customer complaint in
      // Trust OPM; its number is the reference the desk will quote.
      const no = res?.complaint?.no;
      const call = form.callback ? ` A TrustLab doctor will call you on ${String(form.callbackPhone).trim()} shortly.` : '';
      setToast({
        open: true,
        msg: (no ? `Query submitted. Complaint reference ${no}.` : 'Query submitted successfully.') + call,
        tone: 'success',
      });
      setTimeout(() => onBack?.(), call ? 3200 : (no ? 1600 : 900));
    } catch (err) {
      const detail = err?.data?.message || err?.error || 'Submission failed. Please try again.';
      setToast({ open: true, msg: detail, tone: 'error' });
    }
  };

  // The one-line answer the rail shows under each finished step.
  const summaries = [
    nature ? (isPatient ? 'Patient-Specific' : 'General Service') : null,
    stepDone(1)
      ? [isPatient && form.selectedVisit ? form.selectedVisit.patient : null, form.branch]
        .filter(Boolean).join(' · ')
      : null,
    form.description.trim()
      ? `${form.description.trim().slice(0, 42)}${form.description.trim().length > 42 ? '…' : ''}`
      : null,
  ];

  const STEPS = [
    { label: 'Query type',  hint: 'How to triage it' },
    { label: 'Event details', hint: 'When and where' },
    { label: 'Description', hint: 'What happened' },
  ];

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-start">
        {/* ── Left rail: the steps, and what has been answered ── */}
        <Grid item xs={12} md={3.5} lg={3}>
          <SectionCard sx={{ position: { md: 'sticky' }, top: { md: 76 } }}>
            <CardHeader title="Raise a Query" subtitle={`Step ${step + 1} of ${STEPS.length}`} />
            <Box sx={{ p: '15px' }}>
              {STEPS.map((st, n) => {
                const done = stepDone(n) && n < step;
                const current = n === step;
                const reachable = canReach(n);
                return (
                  <Box
                    key={STEP_IDS[n]}
                    onClick={() => go(n)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      gap: 1.25,
                      px: 1,
                      py: 1,
                      mb: '2px',
                      borderRadius: MP.radius,
                      cursor: reachable ? 'pointer' : 'default',
                      bgcolor: current ? MP.tealLight : 'transparent',
                      opacity: reachable ? 1 : 0.45,
                      '&:hover': { bgcolor: current ? MP.tealLight : (reachable ? MP.bg : 'transparent') },
                    }}
                  >
                    {/* The connector runs between the markers, not behind the
                        rows, so a long summary can't push it out of line. */}
                    {n < STEPS.length - 1 && (
                      <Box sx={{
                        position: 'absolute', left: 19, top: 30, bottom: -6,
                        // '1px', NOT 1: sx reads a bare 0-1 as a percentage,
                        // so `width: 1` would paint a full-width block.
                        width: '1px', bgcolor: MP.border,
                      }} />
                    )}
                    <Box
                      sx={{
                        width: 22, height: 22, flexShrink: 0, mt: '1px',
                        borderRadius: '50%',
                        display: 'grid', placeItems: 'center',
                        fontFamily: MONO, fontSize: 10.5, fontWeight: 700,
                        zIndex: 1,
                        bgcolor: done ? MP.teal : (current ? MP.white : MP.borderSoft),
                        color: done ? MP.white : (current ? MP.teal : MP.ink3),
                        border: current ? `1px solid ${MP.teal}` : '1px solid transparent',
                      }}
                    >
                      {done ? <BiIcon name="check-lg" size={12} /> : n + 1}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{
                        fontSize: 12.5,
                        fontWeight: current || done ? 700 : 500,
                        color: current || done ? MP.dark : MP.textMuted,
                        lineHeight: 1.3,
                      }}>
                        {st.label}
                      </Typography>
                      <Typography noWrap sx={{
                        fontSize: 11,
                        color: summaries[n] ? MP.teal : MP.ink3,
                        mt: '2px',
                      }}>
                        {summaries[n] || st.hint}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </SectionCard>
        </Grid>

        {/* ── Right panel: one step at a time ── */}
        <Grid item xs={12} md={8.5} lg={9}>
          <SectionCard>
            <CardHeader
              title={STEPS[step].label}
              subtitle={STEPS[step].hint}
              right={missingByStep[step].length
                ? <RemainingChip n={missingByStep[step].length} />
                : <RemainingChip n={0} />}
            />

            <Box sx={{ p: '15px', minHeight: 320 }}>
              {step === 0 && (
                <>
                  <Alert tone="info" sx={{ mb: 2 }}>
                    All queries are acknowledged within 2 hours. High-priority
                    queries are escalated to the Lab Head immediately.
                  </Alert>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TypeCard
                        active={isPatient}
                        onClick={() => setNature('patient')}
                        icon="file-earmark-medical-fill"
                        tint="#5FD3F3"
                        title="Patient-Specific"
                        subtitle="Linked to one report"
                        bullets={[
                          'Wrong or delayed test result',
                          'Haemolysed / rejected sample',
                          'Missing test in report',
                          'Incorrect reference range',
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TypeCard
                        active={nature === 'general'}
                        onClick={() => setNature('general')}
                        icon="megaphone-fill"
                        tint="#FF9E4A"
                        title="General Service"
                        subtitle="About TrustLab service"
                        bullets={[
                          'Recurring TAT / SLA breach',
                          'Collection team behaviour',
                          'Billing or invoicing issue',
                          'Communication breakdown',
                        ]}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {step === 1 && (
                <Grid container spacing={2}>
                  {isPatient && (
                    <Grid item xs={12}>
                      <FieldLabel required>Select Patient</FieldLabel>
                      <PatientPicker
                        clientId={clientId}
                        value={form.selectedVisit}
                        onChange={(visit) => setForm((f) => ({ ...f, selectedVisit: visit }))}
                      />
                      {form.selectedVisit ? (
                        <Box sx={{
                          mt: 1, px: 1.5, py: 1,
                          bgcolor: MP.tealLight, border: `1px solid ${MP.tealMid}`,
                          borderRadius: MP.radius,
                          display: 'flex', alignItems: 'center', gap: 1,
                        }}>
                          <Box sx={{ color: MP.teal, display: 'flex' }}>
                            <BiIcon name="check-circle-fill" size={15} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: MP.dark }} noWrap>
                              {form.selectedVisit.patient} · {form.selectedVisit.uhid}
                            </Typography>
                            <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.teal }} noWrap>
                              Visitor ID: {form.selectedVisit.visitNo} · {prettyDate(form.selectedVisit.billDate)}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                          Scroll the list to load older visits. Visitor ID is attached automatically.
                        </Typography>
                      )}
                    </Grid>
                  )}

                  {/* Date and time are ONE field, not two grid cells.
                      Split across cells they took two of the row's three
                      columns and pushed Detector against the time spinner,
                      so no two labels in the row started at the same x.
                      Grouped, the step is a clean two-column form.

                      Two inputs rather than a datetime-local: that popup
                      stays open after you pick a day because it is still
                      waiting on the time half, so it only closes on an
                      outside click. A plain date input closes on the day. */}
                  <Grid item xs={12}>
                    <FieldLabel required>Date &amp; Time Detected</FieldLabel>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        type="date"
                        size="small"
                        value={datePart(form.detectedAt)}
                        onChange={(e) => setForm((x) => ({
                          ...x, detectedAt: joinDateTime(e.target.value, timePart(x.detectedAt)),
                        }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...fieldSx, flex: 1 }}
                      />
                      <TextField
                        type="time"
                        size="small"
                        value={timePart(form.detectedAt)}
                        onChange={(e) => setForm((x) => ({
                          ...x, detectedAt: joinDateTime(datePart(x.detectedAt), e.target.value),
                        }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...fieldSx, width: 116, flexShrink: 0 }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                      Defaults to now — change it if the issue was spotted earlier.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldLabel required>Detector — Name &amp; Role</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      placeholder="e.g. Ms. Lakshmi, Senior Technologist"
                      value={form.detector}
                      onChange={set('detector')}
                      sx={fieldSx}
                    />
                    <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                      From your account — edit if someone else detected it.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FieldLabel required>Branch &amp; Section</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      placeholder="Centre and section"
                      value={form.branch}
                      onChange={set('branch')}
                      sx={fieldSx}
                    />
                    <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                      Your centre. Add the section (e.g. “— Biochemistry”) if it helps.
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {step === 2 && (
                <Grid container spacing={2}>
                  {/* Category first: it is the thing that routes the query,
                      and picking it narrows the sub-category below. */}
                  <Grid item xs={12} sm={subOptions.length > 0 ? 6 : 12}>
                    <FieldLabel required>Issue Category</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      value={form.category}
                      onChange={set('category')}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (v) => {
                          const c = categories.find((x) => x.key === v);
                          return c ? c.title : 'Select issue type…';
                        },
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="" disabled>Select issue type…</MenuItem>
                      {categories.map((c) => (
                        <MenuItem key={c.key} value={c.key}>
                          {c.icon ? `${c.icon}  ` : ''}{c.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Only when the chosen category actually has any —
                      an empty dropdown reads as broken. */}
                  {subOptions.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>What specifically?</FieldLabel>
                      <TextField
                        select fullWidth size="small"
                        value={form.subcategory}
                        onChange={set('subcategory')}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (v) => {
                            if (v === OTHER_SUBCATEGORY) return 'Other';
                            const sc = subOptions.find((x) => x.key === v);
                            return sc ? sc.title : 'Optional';
                          },
                        }}
                        sx={fieldSx}
                      >
                        <MenuItem value="">Optional</MenuItem>
                        {subOptions.map((sc) => (
                          <MenuItem key={sc.key} value={sc.key}>
                            {sc.icon ? `${sc.icon}  ` : ''}{sc.title}
                          </MenuItem>
                        ))}
                        {/* Always last. The admin list can never cover every
                            case, and without this someone picks whichever
                            option is closest — worse than their own words. */}
                        <MenuItem value={OTHER_SUBCATEGORY}>Other</MenuItem>
                      </TextField>
                    </Grid>
                  )}

                  {form.subcategory === OTHER_SUBCATEGORY && (
                    <Grid item xs={12}>
                      <FieldLabel required>Tell us in a few words</FieldLabel>
                      <TextField
                        fullWidth size="small"
                        value={form.subcategoryOther}
                        onChange={set('subcategoryOther')}
                        placeholder="e.g. Sample collected from the wrong arm"
                        inputProps={{ maxLength: 120 }}
                        sx={fieldSx}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FieldLabel required>Description</FieldLabel>
                    <TextField
                      fullWidth multiline rows={7}
                      placeholder="Describe the issue in detail. Include specific values, timings, or observations that support the query…"
                      value={form.description}
                      onChange={set('description')}
                      sx={fieldSx}
                    />
                    <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                      {form.description.trim().length} characters. Specific values and
                      timings help the lab close this faster.
                    </Typography>
                  </Grid>
                  {/* Talk to a doctor — a TrustLab doctor calls back about this
                      query; the request lands with the desk in Trust OPM. */}
                  <Grid item xs={12}>
                    <Box sx={{
                      border: `1px solid ${form.callback ? MP.teal : MP.border}`,
                      bgcolor: form.callback ? MP.tealLight : MP.white,
                      borderRadius: '6px', px: 2, py: 1.5, transition: 'all .15s',
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ fontSize: 20, lineHeight: 1 }}>📞</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: MP.dark }}>
                            Talk to a TrustLab doctor
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: MP.textMuted }}>
                            Switch on and one of our doctors will call you about this query.
                          </Typography>
                        </Box>
                        <Switch
                          checked={form.callback}
                          onChange={(e) => setForm((f) => ({ ...f, callback: e.target.checked }))}
                          inputProps={{ 'aria-label': 'Ask a TrustLab doctor to call me' }}
                        />
                      </Stack>
                      {form.callback && (
                        <Box sx={{ mt: 1.5, maxWidth: 320 }}>
                          <FieldLabel required>Call me on</FieldLabel>
                          <TextField
                            fullWidth size="small" type="tel"
                            placeholder="10-digit mobile number"
                            value={form.callbackPhone}
                            onChange={set('callbackPhone')}
                            sx={{ ...fieldSx, bgcolor: MP.white }}
                          />
                          <Typography sx={{ fontSize: 11, color: MP.ink3, mt: 0.5 }}>
                            From your account — change it if you would rather be called elsewhere.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FieldLabel>Supporting Evidence (Optional)</FieldLabel>
                    <EvidenceUpload
                      files={form.evidence}
                      onFiles={(files) => setForm((x) => ({ ...x, evidence: files }))}
                    />
                  </Grid>
                </Grid>
              )}

            </Box>

            {/* Step footer */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
              sx={{ px: '15px', py: '11px', borderTop: `1px solid ${MP.border}`, bgcolor: MP.bg }}
            >
              <Button
                variant="outlined"
                onClick={() => (step === 0 ? onBack?.() : setStep(step - 1))}
                startIcon={<BiIcon name={step === 0 ? 'x-lg' : 'arrow-left'} size={13} />}
                sx={{
                  textTransform: 'none', fontWeight: 500, fontSize: 13,
                  borderRadius: MP.radius, color: MP.textMuted, borderColor: MP.border,
                  '&:hover': { borderColor: MP.ink3, color: MP.dark, bgcolor: MP.white },
                }}
              >
                {step === 0 ? 'Cancel' : 'Back'}
              </Button>

              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                {missingByStep[step].length > 0 && (
                  <Typography noWrap sx={{ fontSize: 12, color: MP.textMuted, display: { xs: 'none', sm: 'block' } }}>
                    Needs: <Box component="span" sx={{ color: MP.dark, fontWeight: 600 }}>
                      {missingByStep[step].join(', ')}
                    </Box>
                  </Typography>
                )}
                {step < STEPS.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={() => setStep(step + 1)}
                    disabled={missingByStep[step].length > 0}
                    endIcon={<BiIcon name="arrow-right" size={13} />}
                    sx={{
                      textTransform: 'none', fontWeight: 500, fontSize: 13,
                      borderRadius: MP.radius, bgcolor: MP.teal, color: MP.white,
                      '&:hover': { bgcolor: MP.tealDark },
                      '&.Mui-disabled': { bgcolor: MP.borderSoft, color: MP.ink3 },
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!allDone || isLoading}
                    startIcon={isLoading ? null : <BiIcon name="send-fill" size={13} />}
                    sx={{
                      textTransform: 'none', fontWeight: 500, fontSize: 13,
                      borderRadius: MP.radius, bgcolor: MP.teal, color: MP.white,
                      '&:hover': { bgcolor: MP.tealDark },
                      '&.Mui-disabled': { bgcolor: MP.borderSoft, color: MP.ink3 },
                    }}
                  >
                    {isLoading ? 'Submitting…' : 'Submit query'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <SubmittingDialog
        open={isLoading}
        hasFiles={form.evidence.length > 0}
        fileCount={form.evidence.length}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.tone}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};


// ── Type picker card ──────────────────────────────────────────
// Selected = a ring plus a tick, not just a border colour. Colour alone is
// a poor "chosen" signal: it is the one cue a colour-blind user may not
// get, and on a page this pale a border reads as decoration.
const TypeCard = ({ active, onClick, icon, tint, title, subtitle, bullets }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    sx={{
      position: 'relative',
      height: '100%',
      p: '15px',
      cursor: 'pointer',
      borderRadius: MP.radius,
      bgcolor: active ? MP.tealLight : MP.white,
      border: `1px solid ${active ? MP.teal : MP.border}`,
      boxShadow: active ? `0 0 0 2px ${MP.teal}33` : 'none',
      transition: 'background-color .15s, border-color .15s, box-shadow .15s',
      '&:hover': { borderColor: active ? MP.teal : MP.ink3 },
      '&:focus-visible': { outline: `2px solid ${MP.teal}`, outlineOffset: 2 },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 38, height: 38, flexShrink: 0,
          borderRadius: '9px',
          display: 'grid', placeItems: 'center',
          color: tint,
          bgcolor: active ? MP.white : MP.bg,
          border: `1px solid ${active ? MP.tealMid : MP.border}`,
        }}
      >
        <BiIcon name={icon} size={19} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: MP.dark, letterSpacing: '.02em' }}>
          {title}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: MP.ink3, mt: '2px' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 20, height: 20, flexShrink: 0,
          borderRadius: '50%',
          display: 'grid', placeItems: 'center',
          border: `1px solid ${active ? MP.teal : MP.border}`,
          bgcolor: active ? MP.teal : 'transparent',
          color: MP.white,
        }}
      >
        {active && <BiIcon name="check-lg" size={12} />}
      </Box>
    </Stack>

    <Stack spacing={0.5} sx={{ mt: 1.5 }}>
      {bullets.map((b) => (
        <Stack key={b} direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: MP.ink3, mt: '7px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12.5, color: MP.textMuted, lineHeight: 1.5 }}>{b}</Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);

// ── Submitting dialog ─────────────────────────────────────────
// Centered, non-dismissable. Shows while the RCA POST is in flight
// so the user gets immediate feedback even if the submit button is
// scrolled out of view. Rotates a small status line through the
// logical steps (gather → upload → notify) so it doesn't look
// frozen — this is purely decorative; the real submit is a single
// server call.
const SUBMIT_STEPS = (hasFiles, fileCount) => [
  'Preparing your query…',
  hasFiles ? `Uploading ${fileCount} attachment${fileCount === 1 ? '' : 's'}…` : 'Securing the request…',
  'Sending to the lab team…',
  'Almost done…',
];

const SubmittingDialog = ({ open, hasFiles, fileCount }) => {
  const steps = useMemo(() => SUBMIT_STEPS(hasFiles, fileCount), [hasFiles, fileCount]);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!open) { setStepIdx(0); return; }
    const id = setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, steps.length - 1));
    }, 1400);
    return () => clearInterval(id);
  }, [open, steps.length]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      // Override default backdrop so the medical teal reads through
      // — and so the user can't click-dismiss mid-submit.
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(26, 58, 42, 0.55)', backdropFilter: 'blur(3px)' } },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: { xs: '90%', sm: 380 },
          maxWidth: '90vw',
          overflow: 'hidden',
          // Subtle teal gradient border tint at the top.
          borderTop: `3px solid ${MP.teal}`,
          boxShadow: '0 20px 60px rgba(26, 58, 42, 0.25)',
        },
      }}
    >
      <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
        {/* Spinner with a pulsing teal ring behind it */}
        <Box
          sx={{
            position: 'relative',
            width: 88, height: 88, mx: 'auto', mb: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute', inset: 0,
              borderRadius: '50%',
              bgcolor: MP.tealLight,
              animation: 'sd-pulse 1.6s ease-in-out infinite',
              '@keyframes sd-pulse': {
                '0%, 100%': { transform: 'scale(1)',   opacity: 0.6 },
                '50%':      { transform: 'scale(1.15)', opacity: 0.25 },
              },
            }}
          />
          <CircularProgress
            size={56}
            thickness={4}
            sx={{ color: MP.teal, position: 'relative', zIndex: 1 }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: 19,
            letterSpacing: '0.5px',
            color: MP.dark,
            fontWeight: 700,
          }}
        >
          Submitting your query
        </Typography>

        <Typography sx={{ fontSize: 12.5, color: MP.textMuted, mt: 0.75, mb: 2.25, px: 1 }}>
          Please keep this tab open — we're attaching your evidence and notifying the lab team.
        </Typography>

        {/* Rotating status line */}
        <Box
          sx={{
            bgcolor: MP.tealLight,
            border: `1px solid ${MP.tealMid}`,
            borderRadius: '6px',
            px: 1.5, py: 1,
            display: 'flex', alignItems: 'center', gap: 1,
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 6, height: 6, borderRadius: '50%',
              bgcolor: MP.teal,
              animation: 'sd-blink 1s ease-in-out infinite',
              '@keyframes sd-blink': {
                '0%, 100%': { opacity: 0.3 },
                '50%':      { opacity: 1 },
              },
            }}
          />
          <Typography
            sx={{
              fontFamily: MONO, fontSize: 11.5, color: MP.tealDark,
              letterSpacing: '0.03em',
            }}
          >
            {steps[stepIdx]}
          </Typography>
        </Box>

        <LinearProgress
          sx={{
            mt: 2.25, height: 3, borderRadius: 2,
            bgcolor: MP.tealLight,
            '& .MuiLinearProgress-bar': { bgcolor: MP.teal },
          }}
        />
      </Box>
    </Dialog>
  );
};

// ── Evidence upload — custom drop zone + file card ────────────
// Empty state: dashed teal-tinted zone with a cloud icon, primary
// action copy, and allowed file-type hints. Clicking anywhere on
// the zone opens the native file picker (or drop a file onto it).
// Filled state: a solid card showing the file icon/size/name with
// a Remove (×) button to clear the selection.
const humanSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const fileKindIcon = (file) => {
  const name = (file?.name || '').toLowerCase();
  if (/\.pdf$/.test(name)) return <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: '#D93025' }} />;
  if (/\.(png|jpe?g|webp|gif|heic|heif)$/.test(name)) return <ImageOutlinedIcon sx={{ fontSize: 22, color: MP.teal }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: MP.textMuted }} />;
};

const MAX_EVIDENCE_FILES = 10;

// A blob URL per attachment, created when the file list changes and
// REVOKED when it changes again or the form unmounts. The revoke is the
// whole reason this is a hook: without it every re-pick strands a blob in
// memory for the life of the tab, and an evidence upload is exactly where
// someone swaps the same photo three times.
const fileKey = (f) => `${f.name}::${f.size}`;

const useFilePreviews = (files) => {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    const made = {};
    for (const file of files) made[fileKey(file)] = URL.createObjectURL(file);
    setUrls(made);
    return () => { for (const url of Object.values(made)) URL.revokeObjectURL(url); };
  }, [files]);
  return urls;
};

const isImage = (f) => (f.type || '').startsWith('image/');
const isPdf = (f) => (f.type || '') === 'application/pdf' || /\.pdf$/i.test(f.name);

// Full-size look at one attachment. Images render directly; a PDF goes in
// an iframe, which every current browser renders natively - no viewer
// library for something the platform already does.
const PreviewDialog = ({ file, url, onClose }) => (
  <Dialog
    open={!!file}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    PaperProps={{ sx: { borderRadius: MP.radius, bgcolor: MP.white, overflow: 'hidden' } }}
  >
    {file && (
      <>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ px: '15px', py: '9px', minHeight: 48, bgcolor: MP.hd, borderBottom: `2px solid ${MP.lnTop}` }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.hdFg }}>
              {file.name}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'rgba(255,255,255,.82)' }}>
              {humanSize(file.size)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close preview" sx={{ color: 'rgba(255,255,255,.92)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ bgcolor: MP.bg, display: 'grid', placeItems: 'center', p: 2, minHeight: 320 }}>
          {isImage(file) && (
            <Box
              component="img"
              src={url}
              alt={file.name}
              sx={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', borderRadius: MP.radius }}
            />
          )}
          {isPdf(file) && (
            <Box
              component="iframe"
              src={url}
              title={file.name}
              sx={{ width: '100%', height: '70vh', border: 0, borderRadius: MP.radius, bgcolor: MP.white }}
            />
          )}
          {!isImage(file) && !isPdf(file) && (
            <Typography sx={{ fontSize: 13, color: MP.textMuted }}>
              No preview for this file type — it will still be attached.
            </Typography>
          )}
        </Box>
      </>
    )}
  </Dialog>
);

const EvidenceUpload = ({ files, onFiles }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const urls = useFilePreviews(files);

  const open = () => inputRef.current?.click();

  // Merge newly picked files into the existing list, de-duping by
  // name+size (same file re-picked doesn't appear twice) and capping
  // at MAX_EVIDENCE_FILES so the backend limit isn't exceeded.
  const append = (incoming) => {
    if (!incoming || incoming.length === 0) return;
    const keyOf = (f) => `${f.name}::${f.size}`;
    const seen = new Set(files.map(keyOf));
    const fresh = Array.from(incoming).filter((f) => !seen.has(keyOf(f)));
    const combined = [...files, ...fresh].slice(0, MAX_EVIDENCE_FILES);
    onFiles(combined);
  };

  const onPick = (e) => {
    append(e.target.files);
    // Reset so picking the same file twice still fires onChange.
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    append(e.dataTransfer.files);
  };
  const removeAt = (i) => onFiles(files.filter((_, idx) => idx !== i));

  const atLimit = files.length >= MAX_EVIDENCE_FILES;

  return (
    <Box>
      {/* Drop zone — always visible so the user can keep adding. */}
      <Box
        role="button"
        tabIndex={0}
        onClick={!atLimit ? open : undefined}
        onKeyDown={(e) => !atLimit && (e.key === 'Enter' || e.key === ' ') && open()}
        onDragOver={(e) => { if (!atLimit) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={!atLimit ? onDrop : undefined}
        sx={{
          cursor: atLimit ? 'not-allowed' : 'pointer',
          outline: 'none',
          px: 2.5, py: files.length ? 2 : 3,
          border: `1.5px dashed ${dragOver ? MP.teal : MP.border}`,
          bgcolor: dragOver ? MP.tealLight : MP.bg,
          borderRadius: '6px',
          textAlign: 'center',
          transition: 'all .18s',
          opacity: atLimit ? 0.55 : 1,
          '&:hover': !atLimit && { borderColor: MP.teal, bgcolor: MP.tealLight },
        }}
      >
        <Box
          sx={{
            width: 44, height: 44, mx: 'auto', mb: 1.25,
            borderRadius: '50%',
            bgcolor: MP.white, border: `1.5px solid ${MP.tealMid}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 22, color: MP.teal }} />
        </Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: MP.dark }}>
          {atLimit
            ? `Attachment limit reached (${MAX_EVIDENCE_FILES})`
            : (
              <>
                Click to upload{' '}
                <Box component="span" sx={{ color: MP.textMuted, fontWeight: 500 }}>or drag &amp; drop</Box>
              </>
            )}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: MP.textMuted, mt: 0.5 }}>
          PDF, JPG, PNG · max 5 MB each · up to {MAX_EVIDENCE_FILES} files
        </Typography>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={onPick}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Selected files list — each with its own remove button. */}
      {files.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.25 }}>
          {files.map((file, i) => (
            <Box
              key={`${file.name}-${file.size}-${i}`}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1,
                border: `1.5px solid ${MP.tealMid}`,
                bgcolor: MP.tealLight,
                borderRadius: '6px',
              }}
            >
              {/* An image shows itself. Anything else keeps its kind icon -
                  a generic page glyph tells you less than the picture does,
                  and the picture is what someone attached evidence for. */}
              <Box
                onClick={() => setPreview(file)}
                role="button"
                tabIndex={0}
                aria-label={`Preview ${file.name}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreview(file); } }}
                sx={{
                  width: 40, height: 40, borderRadius: MP.radius, flexShrink: 0,
                  overflow: 'hidden', cursor: 'pointer',
                  bgcolor: MP.white, border: `1px solid ${MP.tealMid}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  '&:hover': { borderColor: MP.teal },
                }}
              >
                {isImage(file) && urls[fileKey(file)] ? (
                  <Box
                    component="img"
                    src={urls[fileKey(file)]}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : fileKindIcon(file)}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: MP.dark }} noWrap>
                  {file.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.teal }}>
                    {humanSize(file.size)}
                  </Typography>
                  <Box
                    onClick={() => setPreview(file)}
                    sx={{
                      fontSize: 11.5, fontWeight: 600, color: MP.teal,
                      cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Preview
                  </Box>
                </Stack>
              </Box>
              <IconButton
                size="small"
                onClick={() => removeAt(i)}
                aria-label={`Remove ${file.name}`}
                sx={{ color: MP.textMuted, '&:hover': { color: MP.red, bgcolor: MP.redLight } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <PreviewDialog
        file={preview}
        url={preview ? urls[fileKey(preview)] : null}
        onClose={() => setPreview(null)}
      />
    </Box>
  );
};

// ── Patient picker with infinite-scroll pagination ────────────
// Loads page 1 on mount. When the user scrolls near the bottom of
// the open dropdown, fetches the next page and appends it to the
// accumulated list. RTK Query caches each page separately, so
// re-opening the form reuses already-fetched pages instead of
// re-fetching.
const PatientPicker = ({ clientId, value, onChange }) => {
  const { user } = useAuth();
  const isDoctor = user?.user_type === 'doctor';
  const itdoseDoctorId = user?.doctor?.itdose_doctor_id;
  const [page, setPage] = useState(1);
  const [visits, setVisits] = useState([]);

  // Doctor session sees only their own DoctorID patients; centre admins
  // see every patient under the client. Both hooks always mount but
  // only one fires.
  const centreQuery = useGetSalesByCustomerQuery(
    { customerId: clientId, page },
    { skip: !clientId || isDoctor },
  );
  const doctorQuery = useGetSalesByCustomerAndDoctorQuery(
    { customerId: clientId, doctorId: itdoseDoctorId, page },
    { skip: !clientId || !isDoctor || !itdoseDoctorId },
  );
  const { data, isFetching } = isDoctor ? doctorQuery : centreQuery;

  // Reset whenever the signed-in doctor changes (e.g. fast-swap).
  useEffect(() => {
    setPage(1);
    setVisits([]);
  }, [clientId]);

  // Append newly fetched page into the running list, de-duped by visitNo.
  useEffect(() => {
    if (!data?.data) return;
    const pageVisits = groupByVisit(data.data);
    setVisits((prev) => {
      const seen = new Set(prev.map((v) => v.visitNo));
      const fresh = pageVisits.filter((v) => v.visitNo && !seen.has(v.visitNo));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, [data]);

  const hasNextPage = !!data?.meta?.hasNextPage;

  // Autocomplete listbox onScroll — trigger next page when the user
  // hits the bottom (with a 48px threshold so it fires just before
  // the true end).
  const handleListboxScroll = (e) => {
    const el = e.currentTarget;
    if (!hasNextPage || isFetching) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) setPage((p) => p + 1);
  };

  const filterOptions = (opts, state) => {
    const q = state.inputValue.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((v) => {
      const blob = `${v.patient} ${v.uhid} ${v.visitNo} ${v.tests.map((t) => t.name).join(' ')}`.toLowerCase();
      return blob.includes(q);
    });
  };

  if (!clientId) {
    return (
      <Typography sx={{ fontSize: 12, color: MP.red, py: 1 }}>
        Your account is missing a Client ID. Contact admin to link one.
      </Typography>
    );
  }

  return (
    <Autocomplete
      options={visits}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={(v) => (v ? `${v.patient} · ${v.uhid}` : '')}
      isOptionEqualToValue={(a, b) => a?.visitNo === b?.visitNo}
      filterOptions={filterOptions}
      loading={isFetching && visits.length === 0}
      noOptionsText={isFetching ? 'Loading…' : 'No patients found'}
      ListboxProps={{ onScroll: handleListboxScroll, sx: { maxHeight: 320 } }}
      renderOption={(props, v) => (
        <Box component="li" {...props} key={v.visitNo}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: MP.dark }} noWrap>
              {v.patient}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }} noWrap>
              <Box component="span" sx={{ color: MP.tealDark, fontWeight: 700 }}>{v.uhid}</Box>
              {' · '}{prettyDate(v.billDate)} · {v.visitNo}
            </Typography>
            <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.25 }} noWrap>
              {v.tests.map((t) => t.name).join(', ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search patient name, UHID, or test…"
          sx={fieldSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} sx={{ color: MP.teal, mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

// ── local atoms ───────────────────────────────────────────────



// How many required fields this card is still waiting for. Goes green and
// ticks when the card is done, so progress is visible per section rather
// than only in the footer.
const RemainingChip = ({ n }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontFamily: MONO, fontSize: 10.5, fontWeight: 600,
      bgcolor: n ? 'rgba(255,200,31,.22)' : 'rgba(126,217,87,.22)',
      color: n ? '#FFD45C' : '#A6E88A',
      px: '8px', py: '2px', borderRadius: '20px', whiteSpace: 'nowrap',
    }}
  >
    {n ? `${n} left` : <><BiIcon name="check-lg" size={11} /> done</>}
  </Box>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.white,
    borderRadius: '6px',
    fontSize: 13,
    '& fieldset': { borderColor: MP.border, borderWidth: 1 },
    '&:hover fieldset': { borderColor: MP.ink3 },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1 },
    '&.Mui-focused': { boxShadow: MP.focus },
  },
};

export default RaiseQuery;
